import {
  CheckBoxCheckSolidIcon,
  CheckBoxUnIcon,
  DoneIcon,
} from '@blocksuite/icons/lit';
import type { ReadonlySignal } from '@preact/signals-core';
import { cssVarV2 } from '@streamlain/theme/v2';
import { css, html, type TemplateResult, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { keyed } from 'lit/directives/keyed.js';
import type { ClassInfo } from 'lit-html/directives/class-map.js';
import { ifDefined } from 'lit-html/directives/if-defined.js';

import { MenuFocusable } from './focusable.js';
import {
  isStreamLainDisclosureMenu,
  type Menu,
  STREAMLAIN_DISCLOSURE_MENU_ROW_CLASS,
} from './menu.js';
import type { MenuClass, MenuItemRender } from './types.js';

export type MenuButtonData = {
  accessibleName?: string;
  content: () => TemplateResult;
  class: ClassInfo;
  disabled?: boolean;
  select: (ele: HTMLElement) => void | false;
  onHover?: (hover: boolean) => void;
  testId?: string;
  closeOnSelect?: boolean;
};

export class MenuButton extends MenuFocusable {
  static override styles = css`
    .affine-menu-button {
      display: flex;
      width: 100%;
      box-sizing: border-box;
      font-size: 20px;
      cursor: pointer;
      align-items: center;
      padding: 4px;
      gap: 8px;
      border-radius: 4px;
      color: var(--streamlain-v2-icon-primary);
    }

    .affine-menu-button:hover,
    affine-menu-button.active .affine-menu-button {
      background-color: var(
        --affine-menu-hover-color,
        var(--streamlain-v2-layer-background-hoverOverlay)
      );
    }

    .affine-menu-button.disabled-item {
      cursor: not-allowed;
      opacity: 0.45;
      pointer-events: none;
    }

    .affine-menu-button.data-view-menu-entry {
      padding: 6px 10px;
      gap: 12px;
    }

    .affine-menu-button.data-view-menu-entry
      > :first-child:not(.affine-menu-action-text) {
      width: 20px;
      min-width: 20px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .affine-menu-button .affine-menu-action-text {
      flex: 1;
      min-width: 0;
      text-align: start;
      font-size: 14px;
      line-height: 22px;
      color: var(--streamlain-v2-text-primary);
    }

    .affine-menu-button.streamlain-disclosure-menu-row {
      display: grid;
      grid-template-columns: 20px minmax(0, 1fr) 18px;
      align-items: center;
      min-height: 36px;
      padding: 7px 10px;
      gap: 10px;
      border-radius: 6px;
      font-size: 14px;
      line-height: 20px;
      color: var(
        --streamlain-v2-icon-primary,
        var(--streamlain-v2-icon-primary)
      );
      box-sizing: border-box;
    }

    .affine-menu-button.streamlain-disclosure-menu-row.data-view-menu-entry {
      padding: 7px 10px;
      gap: 10px;
    }

    .affine-menu-button.streamlain-disclosure-menu-row
      > :not(.affine-menu-action-text):first-child {
      grid-column: 1;
      justify-self: center;
      width: 20px;
      min-width: 20px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .affine-menu-button.streamlain-disclosure-menu-row
      .affine-menu-action-text {
      grid-column: 2;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: initial;
      font-size: 14px;
      line-height: 20px;
      color: var(
        --streamlain-v2-text-primary,
        var(--streamlain-v2-text-primary)
      );
    }

    .affine-menu-button.streamlain-disclosure-menu-row
      > :last-child:not(.affine-menu-action-text) {
      grid-column: 3;
      justify-self: center;
      width: 18px;
      min-width: 18px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .affine-menu-button.streamlain-disclosure-menu-row
      .affine-menu-submenu-postfix {
      grid-column: 3;
      justify-self: end;
      width: auto;
      min-width: 18px;
      display: inline-flex;
      align-items: center;
      justify-content: flex-end;
      gap: 4px;
    }

    .affine-menu-button.streamlain-disclosure-menu-row svg {
      width: 18px;
      height: 18px;
      display: block;
    }

    .affine-menu-button.focused {
      outline: 1px solid ${unsafeCSS(cssVarV2.layer.insideBorder.primaryBorder)};
    }

    .affine-menu-button.selected-item {
      background-color: var(
        --affine-menu-hover-color,
        var(--streamlain-v2-layer-background-hoverOverlay)
      );
    }

    .affine-menu-button.delete-item:hover {
      background-color: var(--streamlain-v2-layer-background-error);
      color: var(--streamlain-v2-status-error);
    }

    .affine-menu-button.delete-item:hover .affine-menu-action-text {
      color: var(--streamlain-v2-status-error);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    this.disposables.addFromEvent(this, 'mouseenter', () => {
      this.data.onHover?.(true);
      this.menu.closeSubMenu();
    });
    this.disposables.addFromEvent(this, 'mouseleave', () => {
      this.data.onHover?.(false);
    });
    this.disposables.addFromEvent(this, 'click', this.onClick);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.data.onHover?.(false);
  }

  onClick() {
    if (this.data.disabled) return;
    if (this.data.select(this) !== false) {
      this.menu.options.onComplete?.();
      if (this.data.closeOnSelect !== false) {
        this.menu.close();
      }
    }
  }

  override onPressEnter() {
    this.onClick();
  }

  protected override render(): unknown {
    const classString = classMap({
      'affine-menu-button': true,
      focused: this.isFocused$.value,
      [STREAMLAIN_DISCLOSURE_MENU_ROW_CLASS]: isStreamLainDisclosureMenu(
        this.menu
      ),
      ...this.data.class,
    });
    return html` <div
      class="${classString}"
      role="menuitem"
      aria-disabled=${this.data.disabled ? 'true' : 'false'}
      aria-label=${ifDefined(this.data.accessibleName)}
      data-testid=${ifDefined(this.data.testId)}
    >
      ${this.data.content()}
    </div>`;
  }

  @property({ attribute: false })
  accessor data!: MenuButtonData;
}

export class MobileMenuButton extends MenuFocusable {
  static override styles = css`
    .mobile-menu-button {
      display: flex;
      width: 100%;
      cursor: pointer;
      align-items: center;
      font-size: 20px;
      padding: 11px 8px;
      gap: 8px;
      border-radius: 4px;
      color: var(--streamlain-v2-icon-primary);
    }

    .mobile-menu-button .affine-menu-action-text {
      flex: 1;
      color: var(--streamlain-v2-text-primary);
      font-size: 17px;
      line-height: 22px;
    }

    .mobile-menu-button.streamlain-disclosure-menu-row {
      display: grid;
      grid-template-columns: 20px minmax(0, 1fr) 18px;
      align-items: center;
      min-height: 36px;
      padding: 7px 10px;
      gap: 10px;
      border-radius: 6px;
      font-size: 14px;
      line-height: 20px;
      color: var(
        --streamlain-v2-icon-primary,
        var(--streamlain-v2-icon-primary)
      );
      box-sizing: border-box;
    }

    .mobile-menu-button.streamlain-disclosure-menu-row
      > :not(.affine-menu-action-text):first-child {
      grid-column: 1;
      justify-self: center;
      width: 20px;
      min-width: 20px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .mobile-menu-button.streamlain-disclosure-menu-row
      .affine-menu-action-text {
      grid-column: 2;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: initial;
      font-size: 14px;
      line-height: 20px;
      color: var(
        --streamlain-v2-text-primary,
        var(--streamlain-v2-text-primary)
      );
    }

    .mobile-menu-button.streamlain-disclosure-menu-row
      > :last-child:not(.affine-menu-action-text) {
      grid-column: 3;
      justify-self: center;
      width: 18px;
      min-width: 18px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .mobile-menu-button.streamlain-disclosure-menu-row
      .affine-menu-submenu-postfix {
      grid-column: 3;
      justify-self: end;
      width: auto;
      min-width: 18px;
      display: inline-flex;
      align-items: center;
      justify-content: flex-end;
      gap: 4px;
    }

    .mobile-menu-button.streamlain-disclosure-menu-row svg {
      width: 18px;
      height: 18px;
      display: block;
    }

    .mobile-menu-button.delete-item {
      color: var(--streamlain-v2-status-error);
    }

    .mobile-menu-button.delete-item .mobile-menu-action-text {
      color: var(--streamlain-v2-status-error);
    }

    .mobile-menu-button.disabled-item {
      cursor: not-allowed;
      opacity: 0.45;
      pointer-events: none;
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    this.disposables.addFromEvent(this, 'click', this.onClick);
  }

  // eslint-disable-next-line sonarjs/no-identical-functions
  onClick() {
    if (this.data.disabled) return;
    if (this.data.select(this) !== false) {
      this.menu.options.onComplete?.();
      if (this.data.closeOnSelect !== false) {
        this.menu.close();
      }
    }
  }

  override onPressEnter() {
    this.onClick();
  }

  protected override render(): unknown {
    const classString = classMap({
      'mobile-menu-button': true,
      focused: this.isFocused$.value,
      [STREAMLAIN_DISCLOSURE_MENU_ROW_CLASS]: isStreamLainDisclosureMenu(
        this.menu
      ),
      ...this.data.class,
    });
    return html` <div
      class="${classString}"
      role="menuitem"
      aria-disabled=${this.data.disabled ? 'true' : 'false'}
      aria-label=${ifDefined(this.data.accessibleName)}
      data-testid=${ifDefined(this.data.testId)}
    >
      ${this.data.content()}
    </div>`;
  }

  @property({ attribute: false })
  accessor data!: MenuButtonData;
}

const renderButton = (data: MenuButtonData, menu: Menu) => {
  if (menu.isMobile) {
    return html`<mobile-menu-button
      .data="${data}"
      .menu="${menu}"
    ></mobile-menu-button>`;
  }
  return html`<affine-menu-button
    .data="${data}"
    .menu="${menu}"
  ></affine-menu-button>`;
};
export const menuButtonItems = {
  action:
    (config: {
      name: string;
      label?: () => TemplateResult;
      info?: TemplateResult;
      prefix?: TemplateResult;
      postfix?: TemplateResult;
      isSelected?: boolean;
      select: (ele: HTMLElement) => void | false;
      onHover?: (hover: boolean) => void;
      class?: MenuClass;
      closeOnSelect?: boolean;
      disabled?: boolean;
      hide?: () => boolean;
      testId?: string;
    }) =>
    menu => {
      if (config.hide?.() || !menu.search(config.name)) {
        return;
      }
      const data: MenuButtonData = {
        accessibleName: config.name,
        content: () => {
          return html`
            ${config.prefix}
            <div class="affine-menu-action-text">
              ${config.label?.() ?? config.name} ${config.info}
            </div>
            ${config.postfix ?? (config.isSelected ? DoneIcon() : undefined)}
          `;
        },
        onHover: config.onHover,
        select: config.select,
        closeOnSelect: config.closeOnSelect,
        class: {
          'disabled-item': config.disabled ?? false,
          'selected-item': config.isSelected ?? false,
          ...config.class,
        },
        disabled: config.disabled,
        testId: config.testId,
      };
      return renderButton(data, menu);
    },
  checkbox:
    (config: {
      name: string;
      checked: ReadonlySignal<boolean>;
      postfix?: TemplateResult;
      label?: () => TemplateResult;
      select: (checked: boolean) => boolean;
      class?: ClassInfo;
      testId?: string;
    }) =>
    menu => {
      if (!menu.search(config.name)) {
        return;
      }
      const data: MenuButtonData = {
        accessibleName: config.name,
        content: () => html`
          ${config.checked.value
            ? CheckBoxCheckSolidIcon({ style: `color:#1E96EB` })
            : CheckBoxUnIcon()}
          <div class="affine-menu-action-text">
            ${config.label?.() ?? config.name}
          </div>
          ${config.postfix}
        `,
        select: () => {
          config.select(config.checked.value);
          return false;
        },
        class: config.class ?? {},
        testId: config.testId,
      };
      return html`${keyed(config.name, renderButton(data, menu))}`;
    },
  toggleSwitch:
    (config: {
      name: string;
      on: boolean;
      prefix?: TemplateResult;
      postfix?: TemplateResult;
      label?: () => TemplateResult;
      onChange: (on: boolean) => void;
      class?: ClassInfo;
      testId?: string;
    }) =>
    menu => {
      if (!menu.search(config.name)) {
        return;
      }
      const onChange = (on: boolean) => {
        config.onChange(on);
      };

      const data: MenuButtonData = {
        accessibleName: config.name,
        content: () => html`
          ${config.prefix}
          <div class="affine-menu-action-text">
            ${config.label?.() ?? config.name}
          </div>
          <toggle-switch
            .on="${config.on}"
            .onChange="${onChange}"
          ></toggle-switch>
          ${config.postfix}
        `,
        select: () => {
          config.onChange(config.on);
          return false;
        },
        class: config.class ?? {},
        testId: config.testId,
      };
      return html`${keyed(config.name, renderButton(data, menu))}`;
    },
} satisfies Record<string, MenuItemRender<never>>;
